import {Trans} from '@lingui/macro';
import {isEmpty} from 'lodash';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {usePrevious, useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {DialogFooter} from '../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../components/common/dialogs/dialogHeader';
import {Paragraph} from '../../../components/common/paragraph';
import {ResourceNames, tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {TestDataSidebar} from '../../editors/expressions/testDataSidebar';
import {EditorStateActions} from './hooks/useEditorState';
import {RuleEditorState} from './state/ruleEditorState';

/*
 * Constants.
 */

const dialogWidth = 400; // px.

const VALID_RESOURCES: ResourceNames[] = ['conversation', 'message', 'activity'];

/*
 * Styles.
 */

const StyledDialogContent = styled.div`
  padding: 20px;
`;

const StyledParagraph = styled(Paragraph)`
  margin-top: 20px;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  column-gap: 10px;
`;

/*
 * Component.
 */

interface RuleEditorTestDataSidebarProps {
  ruleEditorState: RuleEditorState;
  actions: EditorStateActions;
  isReadOnly: boolean;
}

export const RuleEditorTestDataSidebar: FC<RuleEditorTestDataSidebarProps> = ({
  ruleEditorState,
  actions,
  isReadOnly,
}) => {
  const {ruleState, expressions, hasDryRunTestData} = ruleEditorState;
  const {exampleResourceId} = ruleState;
  const [isEditing, toggleEditing] = useToggle();
  const previousExampleResourceId = usePrevious(exampleResourceId);

  const onUpdate = useCallback(
    (value: string) => {
      actions.onUpdateExampleResourceId(value);
      toggleEditing();
    },
    [actions, toggleEditing],
  );

  // Dry run again when example resource ID changes.
  useEffect(() => {
    if (exampleResourceId !== previousExampleResourceId) {
      actions.onExecDryRun();
    }
  }, [actions, exampleResourceId, previousExampleResourceId]);

  // No need to show anything if there are no expressions.
  if (isEmpty(expressions)) {
    return null;
  }

  const summary = exampleResourceId && <strong>{exampleResourceId}</strong>;

  return (
    <>
      <TestDataSidebar
        summary={summary}
        hasDryRunTestData={hasDryRunTestData}
        isReadOnly={isReadOnly}
        errorMessage={ruleEditorState.dryRunErrorMessage}
        onEdit={toggleEditing}
        onDryRun={actions.onExecDryRun}
      />
      {isEditing && (
        <RuleEditorTestDataDialog
          exampleResourceId={exampleResourceId}
          onUpdate={onUpdate}
          onRequestClose={toggleEditing}
        />
      )}
    </>
  );
};

interface RuleEditorTestDataDialogProps {
  exampleResourceId?: string;
  onUpdate: HandlerOf<string>;
  onRequestClose: Handler;
}

const RuleEditorTestDataDialog: FC<RuleEditorTestDataDialogProps> = ({
  exampleResourceId,
  onUpdate,
  onRequestClose,
}) => {
  const [value, onChange] = useState(exampleResourceId ?? '');
  const [shouldDisplayError, setDisplayError] = useState(false);

  const errorMessage = useMemo(() => {
    if (!value) {
      return undefined;
    }

    const isValidFrontId = VALID_RESOURCES.some((resourceName) =>
      Boolean(tryParsePublicId(value, resourceName)),
    );

    return isValidFrontId ? undefined : <Trans id="rule-testdata-error-id">Not a valid Front ID</Trans>;
  }, [value]);

  const hasError = Boolean(errorMessage);
  const isDisabled = !value || (hasError && shouldDisplayError);

  const onUpdateClick = useCallback(() => {
    setDisplayError(true);

    if (value && !hasError) {
      onUpdate(value);
    }
  }, [hasError, onUpdate, value]);

  return (
    <Dialog onRequestClose={onRequestClose} width={dialogWidth}>
      <DialogHeader
        title={<Trans id="rule-testdata-title">Example event ID</Trans>}
        onRequestClose={onRequestClose}
      />
      <StyledDialogContent>
        <BoxedInput
          size={VisualSizesEnum.LARGE}
          hasError={hasError}
          errorMessage={errorMessage}
          noMargin
          shouldAutoFocus
        >
          {(childProps) => (
            <BoxedInputElement
              {...childProps}
              type={BoxedInputElementTypesEnum.TEXT}
              placeholder="cnv_123, msg_456"
              value={value ?? ''}
              onChange={onChange}
            />
          )}
        </BoxedInput>
        <StyledParagraph>
          <Trans id="rule-testdata-intro-1">
            Your dynamic variables will be evaluated on the given event. If you enter a Conversation ID, the
            last event matching your triggers will be used.
          </Trans>
        </StyledParagraph>

        <StyledParagraph>
          <InlineLinkButton
            onClick={() =>
              openInBrowser('https://help.front.com/t/h4249w/conversation-id-message-id-and-permalinks')
            }
          >
            <Trans id="rule-testdata-copy-ids">Learn more about copying IDs</Trans>
          </InlineLinkButton>
        </StyledParagraph>
      </StyledDialogContent>
      <DialogFooter>
        <StyledButtons>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onRequestClose}>
            <Trans id="previewer-testdata-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onUpdateClick} isDisabled={isDisabled}>
            <Trans id="previewer-testdata-dryrun">Dry run</Trans>
          </Button>
        </StyledButtons>
      </DialogFooter>
    </Dialog>
  );
};
